import React from "react";
import { useTranslation } from "react-i18next";
import "../assets/styles/TerraformArticle.scss";

function DevOpsArticle() {
  const { i18n } = useTranslation();
  const isEn = i18n.language.startsWith("en");

  if (isEn) {
    return (
      <div className="terraform-article">
        <h1>The DevOps & DevSecOps Role</h1>
        <p style={{ textAlign: "center", color: "var(--text-muted)", fontSize: "0.85rem", marginBottom: "2rem" }}>
          Culture · Practices · Security — April 2025
        </p>

        <h2>What is DevOps?</h2>
        <p>
          DevOps is not a tool, nor a job title. It's a <strong>culture</strong> that aims to
          break down the historical wall between development (Dev) and operations (Ops) teams.
          The goal: deliver software faster, more often and with greater reliability.
        </p>
        <p>
          Concretely, this means automating all stages of an application's lifecycle: from code
          to production, including testing, building, deployment and monitoring.
        </p>

        <h2>The DevOps Cycle</h2>
        <p>
          The DevOps cycle is often represented by an infinite loop (∞), illustrating continuous
          improvement:
        </p>
        <ul>
          <li><strong>Plan</strong> — defining needs and priorities (Jira, GitLab Issues)</li>
          <li><strong>Code</strong> — development and code review (Git, GitLab, GitHub)</li>
          <li><strong>Build</strong> — compilation and artifact construction (Docker, Maven)</li>
          <li><strong>Test</strong> — unit, integration, and performance tests (JUnit, k6)</li>
          <li><strong>Release</strong> — packaging and versioning of deliverables</li>
          <li><strong>Deploy</strong> — automated deployment (Kubernetes, ArgoCD, Terraform)</li>
          <li><strong>Operate</strong> — production and incident management</li>
          <li><strong>Monitor</strong> — monitoring and alerting (Prometheus, Grafana, Zabbix)</li>
        </ul>

        <h2>Technical Pillars of DevOps</h2>
        <p>
          Beyond culture, DevOps relies on concrete technical practices:
        </p>
        <ul>
          <li>
            <strong>CI/CD (Continuous Integration and Deployment)</strong> — each commit
            automatically triggers a test and deployment pipeline. No more risky manual releases.
          </li>
          <li>
            <strong>Infrastructure as Code (IaC)</strong> — infrastructure is described in
            versioned files (Terraform, Ansible). It becomes reproducible, audited and testable
            like code.
          </li>
          <li>
            <strong>GitOps</strong> — Git becomes the single source of truth for infrastructure
            and deployments. ArgoCD or Flux watch the repository and synchronize the cluster.
          </li>
          <li>
            <strong>Containerization and orchestration</strong> — Docker and Kubernetes enable
            portable application packaging and automatic scaling.
          </li>
        </ul>

        <h2>The Rise of DevSecOps</h2>
        <p>
          DevSecOps is the natural evolution of DevOps: it integrates security (<em>Sec</em>)
          at the core of the cycle, from the start. The founding principle is <strong>Shift Left</strong>:
          detect and fix vulnerabilities as early as possible in the cycle, where the cost is lowest.
        </p>
        <p>
          Historically, security came at the end, as a gateway before production. In this model,
          fixing a vulnerability is costly and generates friction. With DevSecOps, every developer
          is responsible for the security of their code.
        </p>

        <h2>Integrating Security into the CI/CD Pipeline</h2>
        <p>
          A typical DevSecOps pipeline integrates several levels of automated analysis:
        </p>
        <pre><code>{`# Example of a DevSecOps GitLab CI pipeline
stages:
  - test
  - sast        # Static code analysis
  - build
  - scan-image  # Docker image scan
  - deploy

sast:
  stage: sast
  image: returntocorp/semgrep
  script:
    - semgrep --config auto src/

container-scan:
  stage: scan-image
  image: aquasec/trivy
  script:
    - trivy image --exit-code 1 --severity HIGH,CRITICAL my-app:$CI_COMMIT_SHA

secrets-detection:
  stage: test
  script:
    - gitleaks detect --source . --exit-code 1`}</code></pre>
        <ul>
          <li><strong>SAST</strong> (Static Application Security Testing) — analyzes source code without executing it (Semgrep, SonarQube)</li>
          <li><strong>SCA</strong> (Software Composition Analysis) — detects vulnerabilities in third-party dependencies</li>
          <li><strong>Image scanning</strong> — Trivy analyzes Docker images for CVEs</li>
          <li><strong>Secret detection</strong> — GitLeaks or TruffleHog prevent committing API keys or passwords</li>
        </ul>

        <h2>DevSecOps Culture in Practice</h2>
        <p>
          Tools are not everything. The success of DevSecOps also relies on:
        </p>
        <ul>
          <li>Training developers on common vulnerabilities (OWASP Top 10)</li>
          <li>Defining <em>Security Gates</em> that block the pipeline if a critical severity level is detected</li>
          <li>Conducting security-oriented code reviews</li>
          <li>Maintaining a dependency inventory and patching regularly</li>
          <li>Practicing the principle of least privilege on all components (IAM, RBAC, secrets)</li>
        </ul>

        <h2>Conclusion</h2>
        <p>
          DevOps has transformed the way software is delivered. DevSecOps is the next logical
          step: building fast <em>and</em> secure systems from the design phase. In a context
          where cyberattacks increasingly target software supply chains, integrating security
          into the pipeline is no longer optional — it's a necessity.
        </p>
      </div>
    );
  }

  return (
    <div className="terraform-article">
      <h1>Le métier du DevOps & DevSecOps</h1>
      <p style={{ textAlign: "center", color: "var(--text-muted)", fontSize: "0.85rem", marginBottom: "2rem" }}>
        Culture · Pratiques · Sécurité — Avril 2025
      </p>

      <h2>Qu'est-ce que le DevOps ?</h2>
      <p>
        Le DevOps n'est pas un outil, ni un poste. C'est une <strong>culture</strong> qui vise à
        casser le mur historique entre les équipes de développement (Dev) et d'exploitation (Ops).
        L'objectif : livrer des logiciels plus rapidement, plus souvent et avec plus de fiabilité.
      </p>
      <p>
        Concrètement, cela se traduit par l'automatisation de toutes les étapes du cycle de vie
        d'une application : du code jusqu'à la production, en passant par les tests, le build,
        le déploiement et la surveillance.
      </p>

      <h2>Le cycle DevOps</h2>
      <p>
        Le cycle DevOps est souvent représenté par une boucle infinie (∞), qui illustre
        l'amélioration continue :
      </p>
      <ul>
        <li><strong>Plan</strong> — définition des besoins et des priorités (Jira, GitLab Issues)</li>
        <li><strong>Code</strong> — développement et revue de code (Git, GitLab, GitHub)</li>
        <li><strong>Build</strong> — compilation et construction des artefacts (Docker, Maven)</li>
        <li><strong>Test</strong> — tests unitaires, d'intégration, de performance (JUnit, k6)</li>
        <li><strong>Release</strong> — packaging et versioning des livrables</li>
        <li><strong>Deploy</strong> — déploiement automatisé (Kubernetes, ArgoCD, Terraform)</li>
        <li><strong>Operate</strong> — gestion de la production, des incidents</li>
        <li><strong>Monitor</strong> — surveillance et alerting (Prometheus, Grafana, Zabbix)</li>
      </ul>

      <h2>Les piliers techniques du DevOps</h2>
      <p>
        Au-delà de la culture, le DevOps repose sur des pratiques techniques concrètes :
      </p>
      <ul>
        <li>
          <strong>CI/CD (Intégration et Déploiement Continus)</strong> — chaque commit déclenche
          automatiquement un pipeline de tests et de déploiement. Plus de livraisons manuelles
          risquées.
        </li>
        <li>
          <strong>Infrastructure as Code (IaC)</strong> — l'infrastructure est décrite dans des
          fichiers versionnés (Terraform, Ansible). Elle devient reproductible, auditée et
          testable comme du code.
        </li>
        <li>
          <strong>GitOps</strong> — Git devient la source de vérité unique pour l'infrastructure
          et les déploiements. ArgoCD ou Flux surveillent le dépôt et synchronisent le cluster.
        </li>
        <li>
          <strong>Conteneurisation et orchestration</strong> — Docker et Kubernetes permettent
          de packager les applications de manière portable et de les scaler automatiquement.
        </li>
      </ul>

      <h2>L'émergence du DevSecOps</h2>
      <p>
        Le DevSecOps est l'évolution naturelle du DevOps : il intègre la sécurité (<em>Sec</em>)
        au cœur du cycle, dès le début. Le principe fondateur est le <strong>Shift Left</strong> :
        détecter et corriger les vulnérabilités le plus tôt possible dans le cycle, là où le coût
        est le plus faible.
      </p>
      <p>
        Historiquement, la sécurité intervenait à la fin, comme une porte d'entrée avant la
        production. Dans ce modèle, corriger une vulnérabilité est coûteux et génère des frictions.
        Avec le DevSecOps, chaque développeur est responsable de la sécurité de son code.
      </p>

      <h2>Intégrer la sécurité dans le pipeline CI/CD</h2>
      <p>
        Un pipeline DevSecOps type intègre plusieurs niveaux d'analyse automatisée :
      </p>
      <pre><code>{`# Exemple de pipeline GitLab CI DevSecOps
stages:
  - test
  - sast        # Analyse statique du code
  - build
  - scan-image  # Scan de l'image Docker
  - deploy

sast:
  stage: sast
  image: returntocorp/semgrep
  script:
    - semgrep --config auto src/

container-scan:
  stage: scan-image
  image: aquasec/trivy
  script:
    - trivy image --exit-code 1 --severity HIGH,CRITICAL my-app:$CI_COMMIT_SHA

secrets-detection:
  stage: test
  script:
    - gitleaks detect --source . --exit-code 1`}</code></pre>
      <ul>
        <li><strong>SAST</strong> (Static Application Security Testing) — analyse le code source sans l'exécuter (Semgrep, SonarQube)</li>
        <li><strong>SCA</strong> (Software Composition Analysis) — détecte les vulnérabilités dans les dépendances tierces</li>
        <li><strong>Scan d'images</strong> — Trivy analyse les images Docker à la recherche de CVEs</li>
        <li><strong>Détection de secrets</strong> — GitLeaks ou TruffleHog empêchent de committer des clés API ou mots de passe</li>
      </ul>

      <h2>La culture DevSecOps au quotidien</h2>
      <p>
        Les outils ne font pas tout. La réussite d'un DevSecOps repose aussi sur :
      </p>
      <ul>
        <li>Former les développeurs aux vulnérabilités communes (OWASP Top 10)</li>
        <li>Définir des <em>Security Gates</em> qui bloquent le pipeline si un niveau de sévérité critique est détecté</li>
        <li>Réaliser des revues de code orientées sécurité</li>
        <li>Maintenir un inventaire des dépendances et patcher régulièrement</li>
        <li>Pratiquer le principe du moindre privilège sur tous les composants (IAM, RBAC, secrets)</li>
      </ul>

      <h2>Conclusion</h2>
      <p>
        Le DevOps a transformé la façon de livrer les logiciels. Le DevSecOps est la prochaine
        étape logique : construire des systèmes rapides <em>et</em> sécurisés dès la conception.
        Dans un contexte où les cyberattaques ciblent de plus en plus les chaînes d'approvisionnement
        logicielles (supply chain attacks), intégrer la sécurité au pipeline n'est plus une option
        — c'est une nécessité.
      </p>
    </div>
  );
}

export default DevOpsArticle;
